import React, { useEffect, useState } from "react";
import { ClientOnly } from "./components/client-only";
import { Skeleton } from "./components/ui/skeleton";
import { cn } from "./lib/utils";

export interface ImageWithFallbackProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  imageClassName?: string;
}

/** URLs that have loaded successfully this session. Skip skeleton on remount (e.g. back navigation). */
const loadedUrls = new Set<string>();

/**
 * ImageWithFallback component
 */
export const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({
  className,
  imageClassName,
  ...props
}: ImageWithFallbackProps) => {
  const src = props.src ?? "";
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(() => !loadedUrls.has(src));

  useEffect(() => {
    setError(false);
    const alreadyLoaded = loadedUrls.has(src);
    setLoading(!alreadyLoaded);
  }, [src]);

  const handleError = () => {
    setError(true);
    setLoading(false);
  };

  const handleLoad = () => {
    if (src) loadedUrls.add(src);
    setLoading(false);
  };

  return (
    <ClientOnly
      fallback={
        <div className={cn("relative", className)}>
          <Skeleton className="absolute inset-0 w-full h-full" />
        </div>
      }
    >
      <div className={cn("relative", className)}>
        {loading && (
          <Skeleton className="absolute inset-0 w-full h-full" />
        )}
        <img
          {...props}
          className={imageClassName}
          alt={props.alt ?? ""}
          src={error ? "/images/placeholder.png" : props.src}
          onError={handleError}
          onLoad={handleLoad}
        />
      </div>
    </ClientOnly>
  );
};
